import LoadingWheel from 'components/core/LoadingWheel/LoadingWheel';
import { usePaymentStatus } from 'contexts/PaymentContext/PaymentContext.hooks';
import { IPaymentStatus } from 'contexts/PaymentContext/PaymentContext.types';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ITenderAndCheckoutRequest, TenderAndCheckout } from './ProcessPayment.api';
import { useCart } from '../../contexts/CartContext/CartContext.hooks';
import { Config } from 'config';
import { stringToBoolean } from 'utils/typeutils';
import { SendAppErrorEmail } from 'utils/notifications';
import ReactPixel from 'react-facebook-pixel';
import { SendEmailByTemplate } from 'api/Email/Email.api';
import { getComplianzConsent } from 'utils/tracking';

const ProcessPayment = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { cart, isCartLoaded, updateCartStepIndex } = useCart();
    const { setStatus } = usePaymentStatus();

    const getClientInfo = () => {
        return {
            userAgent: navigator.userAgent,
            referrer: document.referrer,
            queryString: location.search
        };
    };

    const tenderCallback = async (
        isSuccessful: boolean,
        message: string = '',
        paymentStatus: IPaymentStatus | null = null
    ) => {
        // Get consent status
        const consent = getComplianzConsent();

        if (isSuccessful) {
            if (consent.marketing === 'allow' && typeof ReactPixel.trackCustom === 'function') {
                ReactPixel.trackCustom('PaymentProcessingComplete', { cart: cart });
            }
            updateCartStepIndex(7);
        } else {
            SendEmailByTemplate(Config.paymentFailureEmail, 'PaymentFailure', 'Payment failure', {
                cart: cart,
                message: message,
                paymentStatus: paymentStatus
            });
            updateCartStepIndex(6);
        }
        navigate('/cart');
    };

    useEffect(() => {
        // Do not run any logic until the cart has been loaded from session storage.
        if (!isCartLoaded) {
            return; // Exit the effect if the cart isn't ready
        }

        (async () => {
            try {
                // Check for the param before we try to use it.
                // This implements the "silent fail" for crawlers/direct hits.
                const tranSuccessParam = searchParams.get('TRANSUCCESS');
                if (!tranSuccessParam) {
                    console.warn(
                        'ProcessPayment: No TRANSUCCESS param found. Redirecting to cart.'
                    );
                    navigate('/cart');
                    return; // Exit the async function
                }

                // Per TEMPUS Docs:
                // TRANSUCCESS: Details the overall result of the request sent to Tempus. This value should be used to
                // determine if a request sent to Tempus was successful (returned as 'TRUE') or not
                // (returned as 'FALSE').
                //
                // MZuber@18.Oct.2024: All cases of empty "Payment Failure" emails have all had empty querystrings.
                // In such cases stringToBoolean would throw an error since there's no TRANSUCCESS param to parse.
                // These scenarios are likely caused by a user or web crawler stumbling directly onto this page.
                // If so, there's no point in treating this like a failed transaction since there's no cart
                // or query string params to look at. Instead, we let the error block below bypass tendering
                // entirely and send an email notification to the devs.
                const tranSuccess = stringToBoolean(tranSuccessParam);

                const status: IPaymentStatus = {
                    approvalCode: searchParams.get('APPROVALCODE') ?? '',
                    tranSuccess: tranSuccess,
                    systemCode: Number(searchParams.get('CCSYSTEMCODE') ?? '0'),
                    avsResp: searchParams.get('CCAVSRESP') ?? '',
                    tranId: searchParams.get('TRANIDENT') ?? '',
                    txnAuth: searchParams.get('TXNAUTH') ?? '',
                    account: searchParams.get('CCACCOUNT') ?? '',
                    cardType: searchParams.get('CCCARDTYPE') ?? '',
                    name: searchParams.get('CCNAME') ?? '',
                    transArmorToken: Number(searchParams.get('TRANSARMORTOKEN')),
                    transArmorTokenProviderId: Number(
                        searchParams.get('TRANSARMORTOKENPROVIDERID')
                    ),
                    expiration: Number(searchParams.get('CCEXP')),
                    avsData: Number(searchParams.get('AVSDATA')),
                    tranRespMessage: searchParams.get('TRANRESPMESSAGE') ?? '',
                    sessionId: searchParams.get('SESSIONID') ?? '',
                    cvvResp: searchParams.get('CVVRESP') ?? '',
                    nonAuthReason: searchParams.get('NONAUTHREASON') ?? '',
                    errorCode: Number(searchParams.get('ERRORCODE') ?? '0')
                };

                setStatus(status);

                if (tranSuccess) {
                    const request: ITenderAndCheckoutRequest = {
                        amountPaidViaCreditCard: Number(sessionStorage.getItem('due')),
                        approvalCode: status.approvalCode,
                        cardNumber: status.account,
                        creditCardTypeName: status.cardType,
                        expirationDate: new Date(status.expiration),
                        token: status.transArmorToken.toString()
                    };

                    const cartId = sessionStorage.getItem('idiCartId');
                    TenderAndCheckout(
                        Number(cartId),
                        request,
                        async () => await tenderCallback(true),
                        async (reason) => {
                            await tenderCallback(
                                false,
                                `General API failure for Cart ID: ${cartId}; Reason: ${JSON.stringify(reason)}; Request: ${JSON.stringify(request)}`,
                                status
                            );
                        }
                    );
                } else {
                    await tenderCallback(
                        false,
                        status?.tranRespMessage || status?.nonAuthReason,
                        status
                    );
                }
            } catch (error) {
                // Log the full error object to the console for live debugging
                console.error('Failed during payment processing:', error);

                // Prepare client info for the email
                const clientInfo = getClientInfo();
                const clientInfoString = `Client Information:\n${JSON.stringify(
                    clientInfo,
                    null,
                    2
                )}`;

                // Get useful details from the error for the email
                let errorDetails = 'An unknown error occurred.';
                if (error instanceof Error) {
                    // error.stack is best for an email log
                    errorDetails = error.stack || error.message;
                } else {
                    // Fallback for non-Error types
                    errorDetails = String(error);
                }

                // Combine and send the error email
                const fullMessage = `Error Details:\n${errorDetails}\n\n${clientInfoString}`;
                SendAppErrorEmail('ProcessPayment', fullMessage);
            }
        })();
    }, [isCartLoaded, cart, navigate, searchParams]);

    if (!isCartLoaded) {
        return <LoadingWheel />;
    }

    return <LoadingWheel />;
};

export default ProcessPayment;
